import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, MapPinned, Save } from 'lucide-react';
import { api } from '@/api/client';

type ZoneTat = { minDays: number; maxDays: number };

const B2C_ROWS: { key: string; label: string; hint: string }[] = [
  { key: 'CITY',         label: 'Within City',         hint: 'Source & destination in the same city (auto-detected)' },
  { key: 'STATE',        label: 'Within State',        hint: 'Same state, different city (auto-detected)' },
  { key: 'METRO',        label: 'Metro',               hint: 'Both pincodes flagged as metro' },
  { key: 'ZONE',         label: 'Within Zone',         hint: 'Same regional zone flag on both pincodes' },
  { key: 'ROI',          label: 'Rest of India (ROI)', hint: 'Default — when none of the above match' },
  { key: 'SPECIAL_DEST', label: 'Special Destination', hint: 'Pincodes flagged Special Destination (incl. NE)' },
];

const B2B_ROWS: { key: string; label: string; hint: string }[] = [
  { key: 'CITY',       label: 'Within City',  hint: 'Same city (auto-detected)' },
  { key: 'STATE',      label: 'Within State', hint: 'Same state (auto-detected)' },
  { key: 'NORTH',      label: 'North',        hint: 'Pincodes flagged N during upload' },
  { key: 'SOUTH',      label: 'South',        hint: 'Pincodes flagged S during upload' },
  { key: 'WEST',       label: 'West',         hint: 'Pincodes flagged W during upload' },
  { key: 'EAST',       label: 'East',         hint: 'Pincodes flagged E during upload' },
  { key: 'NORTH_EAST', label: 'North East',   hint: 'Pincodes flagged NE or Special Destination' },
];

export function TatZonesPanel() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'B2C' | 'B2B'>('B2C');
  const [zones, setZones] = useState<Record<string, ZoneTat> | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['tat-zones', tab],
    queryFn: async () => (await api.get<Record<string, ZoneTat>>(`/tat/zones/${tab}`)).data,
  });

  useEffect(() => { if (data) setZones(data); }, [data, tab]);

  const save = useMutation({
    mutationFn: async () => (await api.put(`/tat/zones/${tab}`, zones)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tat-zones'] });
      toast.success(`${tab} TAT zones saved`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const setCell = (key: string, field: 'minDays' | 'maxDays', v: string) => {
    if (!zones) return;
    setZones({ ...zones, [key]: { ...zones[key], [field]: Number(v) || 0 } });
  };

  const rows = tab === 'B2C' ? B2C_ROWS : B2B_ROWS;

  if (isLoading || !zones) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><MapPinned size={18} /> TAT Zones</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Default TAT (in days) by destination zone. Used when no pincode-specific TAT is configured for the courier.
          B2C uses the same 6 zones as the pricing plan (City / State / Metro / Zone / ROI / Special Destination).
          B2B uses geographic zones (City / State / N / S / E / W / NE) — flag pincodes with <code>N / S / E / W / NE</code> during upload.
        </p>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {(['B2C', 'B2B'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-1.5 text-sm rounded-md transition ${tab === t ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5">Zone</th>
              <th className="text-center px-4 py-2.5 w-32">Min Days</th>
              <th className="text-center px-4 py-2.5 w-32">Max Days</th>
              <th className="text-left px-4 py-2.5">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const z = zones[row.key] ?? { minDays: 0, maxDays: 0 };
              return (
                <tr key={row.key} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-900">{row.label}</td>
                  <td className="px-4 py-2 text-center">
                    <input type="number" min={0} value={z.minDays}
                      onChange={(e) => setCell(row.key, 'minDays', e.target.value)}
                      className="w-20 border rounded px-2 py-1.5 text-sm text-center" />
                  </td>
                  <td className="px-4 py-2 text-center">
                    <input type="number" min={0} value={z.maxDays}
                      onChange={(e) => setCell(row.key, 'maxDays', e.target.value)}
                      className="w-20 border rounded px-2 py-1.5 text-sm text-center" />
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{row.hint}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="px-5 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 disabled:opacity-60 flex items-center gap-1.5"
        >
          <Save size={14} /> {save.isPending ? 'Saving…' : `Save ${tab} TAT Zones`}
        </button>
      </div>

      <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 text-xs text-blue-900">
        <b>How it resolves:</b> Same city → <b>City</b>; same state → <b>State</b>;
        {tab === 'B2C' ? (
          <> then for B2C: both metros → <b>Metro</b>, same regional zone → <b>Within Zone</b>, special destination → <b>SD</b>, else → <b>ROI</b>.</>
        ) : (
          <> for B2B: destination pincode's flag (N/S/E/W/NE) decides the zone.</>
        )}
        {' '}A pincode-specific TAT set on Serviceability per courier always overrides this.
      </div>
    </div>
  );
}
